<template>
  <div class="cr-panel">
    <div v-if="!step" class="cr-panel__empty">
      <i class="tim-icons icon-bullet-list-67"></i>
      Pick a step to see its details
    </div>

    <template v-else>
      <div>
        <div class="cr-panel__kicker">Step {{ index + 1 }} of {{ total }}<span v-if="!step.is_optional"> · Required</span></div>
        <h4 class="cr-panel__title">{{ step.name }}</h4>
        <div v-if="step.step_description" class="cr-panel__desc cr-panel__rich mt-2" v-html="renderInstructions(step.step_description)"></div>
      </div>
      <div v-if="refImages.length" class="cr-panel__imgs">
        <el-image v-for="(url, i) in refImages" :key="'RF' + i" class="cr-panel__img" :src="url"
                  :preview-src-list="refImages" :initial-index="i" :z-index="3000"></el-image>
      </div>

      <info-hint v-if="invalid" variant="danger" icon="icon-alert-circle-exc">
        {{ needsResponse ? 'This step needs a response before the checklist can be completed' : 'This step must be done before the checklist can be completed' }}
      </info-hint>

      <!-- Read-only view (run details) -->
      <template v-if="readonly">
        <div>
          <label class="cr-panel__label">Tags</label>
          <tag-picker :tags="step.tags || []" :catalog="catalog" :saving="tagsSaving" :readonly="!canTag"
                      @add="$emit('add-tag', step, $event)" @remove="$emit('remove-tag', step, $event)"></tag-picker>
        </div>
        <div v-if="responseText">
          <label class="cr-panel__label">Response</label>
          <p class="cr-panel__desc">{{ responseText }}</p>
        </div>
        <div v-if="step.note">
          <label class="cr-panel__label">Note</label>
          <p class="cr-panel__desc">{{ step.note }}</p>
        </div>
        <div v-if="step.issue_flagged === 1">
          <label class="cr-panel__label">Issue</label>
          <p class="cr-panel__desc text-danger">{{ step.issue_description || 'Flagged without a description' }}</p>
        </div>
        <div v-if="step.photo_urls && step.photo_urls.length">
          <label class="cr-panel__label">Photos</label>
          <div class="cr-photos">
            <el-image v-for="(url, i) in step.photo_urls" :key="'RP' + i" :src="url" fit="cover"
                      :preview-src-list="step.photo_urls" :initial-index="i" :z-index="3000" class="cr-photos__thumb"></el-image>
          </div>
        </div>
        <p v-if="!responseText && !step.note && step.issue_flagged !== 1 && !(step.photo_urls && step.photo_urls.length)" class="cr-panel__meta">Nothing was recorded on this step.</p>
        <div v-if="step.work_ticket_id" class="cr-panel__meta">
          <a :href="'/projects/' + step.ticket_project_id + '/tickets/' + step.work_ticket_id" target="_blank" rel="noopener" class="cr-panel__link">
            <i class="tim-icons icon-paper mr-1"></i>{{ step.ticket_project_key ? step.ticket_project_key + '-' : 'Ticket ' }}{{ step.ticket_identifier }}
            <i class="tim-icons el-icon-top-right ml-1" style="font-size: 10px"></i>
          </a>
        </div>
        <div v-else-if="canCreateTicket" class="footer-actions" style="justify-content: flex-start">
          <base-button variant="outlined" tone="primary" @click="$emit('create-ticket', step)">
            <i class="tim-icons icon-paper"></i> Create ticket
          </base-button>
        </div>
      </template>

      <template v-else>
        <!-- Typed response -->
        <base-input v-if="step.response_type === 'dropdown'" label="Response" class="mb-0">
          <el-select class="select-primary" style="width: 100%" clearable placeholder="Choose"
                     :value="step.response_value" @change="emitField('response_value', $event)">
            <el-option v-for="(o, i) in options" :key="'O' + i" class="select-primary" :label="o" :value="o"></el-option>
          </el-select>
        </base-input>
        <base-input v-else-if="step.response_type === 'multiselect'" label="Response" class="mb-0">
          <el-select class="select-primary" style="width: 100%" multiple placeholder="Choose"
                     :value="multiValue" @change="emitField('response_value', $event)">
            <el-option v-for="(o, i) in options" :key="'M' + i" class="select-primary" :label="o" :value="o"></el-option>
          </el-select>
        </base-input>
        <base-input v-else-if="step.response_type === 'text'" label="Response" class="mb-0">
          <textarea class="form-control" rows="2" :value="step.response_value || ''"
                    @input="emitField('response_value', $event.target.value, 'typing')" @change="emitField('response_value', $event.target.value, 'blur')"></textarea>
        </base-input>
        <base-input v-else-if="step.response_type === 'number'" label="Response" class="mb-0">
          <el-input-number :value="numberValue" @change="emitField('response_value', $event)" placeholder="0" class="cr-number"></el-input-number>
        </base-input>

        <!-- Tags -->
        <div>
          <label class="cr-panel__label">Tags</label>
          <tag-picker :tags="step.tags || []" :catalog="catalog" :saving="tagsSaving"
                      @add="$emit('add-tag', step, $event)" @remove="$emit('remove-tag', step, $event)"></tag-picker>
        </div>

        <!-- Note -->
        <base-input label="Note" class="mb-0">
          <textarea class="form-control" rows="2" placeholder="Anything worth recording on this step"
                    :value="step.note || ''" @input="emitField('note', $event.target.value, 'typing')" @change="emitField('note', $event.target.value, 'blur')"></textarea>
        </base-input>

        <!-- Issue -->
        <div>
          <div class="d-flex align-items-center">
            <el-switch active-color="#e14eca" :value="step.issue_flagged === 1" @change="emitField('issue_flagged', $event ? 1 : 0)"></el-switch>
            <label class="text-white mb-0 ml-2">Flag an issue</label>
          </div>
          <base-input v-if="step.issue_flagged === 1" class="mb-0 mt-2">
            <textarea class="form-control" rows="2" placeholder="Broken prop, missing piece, needs maintenance"
                      :value="step.issue_description || ''" @input="emitField('issue_description', $event.target.value, 'typing')"
                      @change="emitField('issue_description', $event.target.value, 'blur')"></textarea>
          </base-input>
        </div>

        <!-- Photos -->
        <div>
          <label class="cr-panel__label">Photos</label>
          <div class="cr-photos">
            <el-image v-for="(url, i) in (step.photo_urls || [])" :key="'P' + i" :src="url" fit="cover"
                      :preview-src-list="step.photo_urls" :initial-index="i" :z-index="3000" class="cr-photos__thumb"></el-image>
            <label class="cr-photos__add">
              <i class="tim-icons" :class="uploading ? 'icon-refresh-02 fa-spin' : 'icon-camera-18'"></i>
              <input type="file" accept="image/*" :disabled="uploading" @change="onPhoto($event)" />
            </label>
          </div>
        </div>
      </template>

      <div v-if="step.completed_at" class="cr-panel__meta">
        Done by {{ step.cb_first_name }} {{ step.cb_last_name }} at {{ formatGroupTime(step.completed_at) }}
      </div>

      <div v-if="!readonly && !hideActions" class="footer-actions">
        <base-button v-if="step.is_completed === 1" variant="outlined" tone="primary" @click="$emit('toggle', step)">
          <i class="tim-icons icon-simple-remove"></i> Mark not done
        </base-button>
        <base-button v-else variant="outlined" tone="success" @click="$emit('toggle', step)">
          <i class="tim-icons icon-check-2"></i> Mark done
        </base-button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupTime } from '@/plugins/dates'
import { renderInstructions } from '@/util/richText'

const props = withDefaults(defineProps<{
  step: any | null
  index: number
  total: number
  invalid?: boolean
  readonly?: boolean
  hideActions?: boolean
  catalog?: any[]
  tagsSaving?: boolean
  canTag?: boolean
  canCreateTicket?: boolean
}>(), { invalid: false, readonly: false, hideActions: false, catalog: () => [], tagsSaving: false, canTag: true, canCreateTicket: false })

const emit = defineEmits<{
  (e: 'field', step: any, field: string, value: any, mode?: 'typing' | 'blur'): void
  (e: 'toggle', step: any): void
  (e: 'error', message: string): void
  (e: 'add-tag', step: any, tagId: number): void
  (e: 'remove-tag', step: any, tag: any): void
  (e: 'create-ticket', step: any): void
}>()

const uploading = ref(false)

// Reference images from the template snapshot; older rows only carry img_url.
const refImages = computed<string[]>(() => {
  const st = props.step
  if (!st) return []
  if (Array.isArray(st.img_urls) && st.img_urls.length) return st.img_urls
  return st.img_url ? [st.img_url] : []
})

const options = computed<string[]>(() => Array.isArray(props.step?.response_options) ? props.step.response_options : [])
const multiValue = computed<string[]>(() => {
  const v = props.step?.response_value
  if (Array.isArray(v)) return v
  try { const arr = JSON.parse(v || '[]'); return Array.isArray(arr) ? arr : [] } catch (e) { return [] }
})
const needsResponse = computed<boolean>(() => {
  const st = props.step
  if (!st || (st.response_type || 'checkbox') === 'checkbox') return false
  const v = st.response_value
  return v === null || v === undefined || String(v).trim() === '' || String(v).trim() === '[]'
})
const responseText = computed<string>(() => {
  const st = props.step
  if (!st || (st.response_type || 'checkbox') === 'checkbox') return ''
  const v = st.response_value
  if (v === null || v === undefined || v === '') return ''
  if (st.response_type === 'multiselect') {
    try { const arr = JSON.parse(v); return Array.isArray(arr) ? arr.join(', ') : String(v) } catch (e) { return String(v) }
  }
  return String(v)
})
const numberValue = computed(() => {
  const v = props.step?.response_value
  return v === null || v === undefined || v === '' ? undefined : Number(v)
})

// Free text passes 'typing' on each keystroke and 'blur' when the runner
// leaves the field; the parent saves on blur only.
function emitField(field: string, value: any, mode?: 'typing' | 'blur') {
  emit('field', props.step, field, value, mode)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function onPhoto(event: any) {
  const file = event.target.files && event.target.files[0]
  if (!file || !props.step) return
  uploading.value = true
  try {
    const config = await authConfig()
    const signed = await axios.post(`${API_LOCATION}upload`, { filePath: file.name, contentType: file.type }, config)
    const uploadUrl: string = signed.data
    await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } })
    const publicUrl = uploadUrl.split('?')[0]
    emit('field', props.step, 'photo_urls', [...(props.step.photo_urls || []), publicUrl])
  } catch (err) {
    emit('error', 'The photo could not be uploaded. Try again.')
  } finally {
    uploading.value = false
    event.target.value = ''
  }
}
</script>

<script lang="ts">
import { Select, Option, Switch, InputNumber } from 'element-ui'

export default {
  name: 'checklist-step-panel',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [InputNumber.name]: InputNumber
  }
}
</script>
